import { normalizeApiError } from '../api/apiError';
import type { ApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PersonalDetails } from '../api/endpoints/personalDetailsApi';
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { PersonalDetailsDto } from "../models/person/personalDetailsDto";
import { FileDto } from "../models/membership/fileDto";
import { FileDetailsParam } from "../../features/myDocuments/models/fileDetailsParam";

interface PersonalDetailsState {
    personalDetailsPersonId: string | null;
    personalDetailsRequestId: string | null;
    personalDetailsError: ApiError | null;
    personalDetails: PersonalDetailsDto | null;
    files: FileDto[] | null;
    downloadStatus: ApiRequestStatus;

    personalDetailsStatus: ApiRequestStatus;
    updatePersonalDetailsStatus: ApiRequestStatus;
    updateVerifyPersonalDetailsStatus: ApiRequestStatus;
    getDocumentsStatus: ApiRequestStatus;
}

const initialState: PersonalDetailsState = {
    personalDetailsPersonId: null,
    personalDetailsRequestId: null,
    personalDetailsError: null,
    personalDetails: null,
    files: null,

    personalDetailsStatus: ApiRequestStatus.Idle,
    updatePersonalDetailsStatus: ApiRequestStatus.Idle,
    updateVerifyPersonalDetailsStatus: ApiRequestStatus.Idle,
    getDocumentsStatus: ApiRequestStatus.Idle,
    downloadStatus: ApiRequestStatus.Idle
}

export const getPersonalDetailsAsync = createReadAsyncThunk<PersonalDetailsDto, string>(
    'prson/getPersonalDetailsAsync',
    async (personId, thunkAPI) => {
        try {
            return await PersonalDetails.getPersonalDetailsAsync(personId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const updatePersonalDetailsStateAsync = createAsyncThunk<void, PersonalDetailsDto>(
    'prson/updatePersonalDetailsStateAsync',
    async (personalDetails, thunkAPI) => {
        try {
            return await PersonalDetails.updatePersonalDetailsAsync(personalDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const updateVerifyPersonalDetailsAsync = createAsyncThunk<void>(
    'prson/updateVerifyPersonalDetailsAsync',
    async (_, thunkAPI) => {
        try {
            return await PersonalDetails.updateVerifyPersonalDetailsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getFilesAsync = createReadAsyncThunk<FileDto[]>(
    'prson/getFilesAsync',
    async (_, thunkAPI) => {
        try {
            return await PersonalDetails.getFilesAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const downloadFileAsync = createReadAsyncThunk<Blob, FileDetailsParam>(
    'prson/downloadFileAsync',
    async (downloadFileParam, thunkAPI) => {
        try {
            return await PersonalDetails.getFileAsync(downloadFileParam.directory, downloadFileParam.fileName);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const personSlice = createSlice({
    name: 'personSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(getPersonalDetailsAsync.pending, (state, action) => {
            if (state.personalDetailsPersonId !== action.meta.arg) state.personalDetails = null;
            state.personalDetailsPersonId = action.meta.arg;
            state.personalDetailsRequestId = action.meta.requestId;
            state.personalDetailsError = null;
            state.personalDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPersonalDetailsAsync.fulfilled, (state, action) => {
            if (state.personalDetailsRequestId !== action.meta.requestId) return;
            state.personalDetailsStatus = ApiRequestStatus.Fulfilled;
            state.personalDetails = action.payload;
            state.personalDetailsRequestId = null;
        });
        builder.addCase(getPersonalDetailsAsync.rejected, (state, action) => {
            if (state.personalDetailsRequestId !== action.meta.requestId) return;
            state.personalDetailsStatus = ApiRequestStatus.Rejected;
            state.personalDetailsError = normalizeApiError(action.payload ?? action.error);
            state.personalDetailsRequestId = null;
        });

        builder.addCase(updatePersonalDetailsStateAsync.pending, (state) => {
            state.updatePersonalDetailsStatus = ApiRequestStatus.Pending;
        });

        builder.addCase(updatePersonalDetailsStateAsync.fulfilled, (state, action) => {
            state.personalDetails = { ...state.personalDetails, ...action.meta.arg };
            state.updatePersonalDetailsStatus = ApiRequestStatus.Fulfilled;
        });

        builder.addCase(updatePersonalDetailsStateAsync.rejected, (state) => {
            state.updatePersonalDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(updateVerifyPersonalDetailsAsync.pending, (state) => {
            state.updateVerifyPersonalDetailsStatus = ApiRequestStatus.Pending;
        });

        builder.addCase(updateVerifyPersonalDetailsAsync.fulfilled, (state) => {
            state.updateVerifyPersonalDetailsStatus = ApiRequestStatus.Fulfilled;
        });

        builder.addCase(updateVerifyPersonalDetailsAsync.rejected, (state) => {
            state.updateVerifyPersonalDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getFilesAsync.pending, (state) => {
            state.getDocumentsStatus = ApiRequestStatus.Pending;
        });

        builder.addCase(getFilesAsync.fulfilled, (state, action) => {
            state.files = action.payload;
            state.getDocumentsStatus = ApiRequestStatus.Fulfilled;
        });

        builder.addCase(getFilesAsync.rejected, (state) => {
            state.getDocumentsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(downloadFileAsync.pending, (state) => {
            state.downloadStatus = ApiRequestStatus.Pending;
        });

        builder.addCase(downloadFileAsync.fulfilled, (state, action) => {
            state.downloadStatus = ApiRequestStatus.Fulfilled;
        });

        builder.addCase(downloadFileAsync.rejected, (state) => {
            state.downloadStatus = ApiRequestStatus.Rejected;
        });
    })
})
